import { memo, useMemo } from 'react'
import { AppGenericRegular, DocumentErrorRegular, SpinnerIosRegular } from '@fluentui/react-icons'
import { useGetSearchParam } from '@/hooks'
import { Link, Outlet, useLocation, useParams } from 'react-router'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { PlatformOutletContext } from './usePlatFormOutletContext'
import { DEFAULT_HEADER_STATE, useSetHeaderContext } from '@/layouts/MainLayout/HeaderContext'
import { OrganisationIcon } from '@/const'

type MessagesWorkspacesWorkspaceCard = RestSchemas['Messages.Workspaces.WorkspaceCard']

export const Platform = memo(() => {
  const params = useParams()
  const projectId = useGetSearchParam<number>('pid')
  const { appKey } = params

  const { getApplicationsApiGetApplications, getWorkspacesApiGetWorkspace } = useRestClient()

  const {
    data: applications,
    isPending: applicationsPending,
    isError: applicationsError,
  } = getApplicationsApiGetApplications.useQuery(undefined, {
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
  })

  const searchedAppKey = applications?.find((app) => app.appKey === appKey)

  const {
    data: workspace,
    isError: workspaceError,
    isPending: workspacePending,
  } = getWorkspacesApiGetWorkspace.useQuery(
    {
      path: { appKey: searchedAppKey?.appKey ?? '', projectId: projectId! },
    },
    {
      enabled: !!searchedAppKey?.appKey && !!projectId,
    }
  )

  const isPending = applicationsPending || (workspacePending && !!searchedAppKey?.appKey)
  const isError = applicationsError || workspaceError

  if (isPending || isError) {
    return (
      <>
        <div className="flex size-full items-center justify-center bg-muted">
          <div className="flex flex-col items-center justify-center gap-y-8">
            {isPending && (
              <>
                <AppGenericRegular fontSize={72} className="text-foreground/50" />
                <div className="flex items-center gap-x-2">
                  <SpinnerIosRegular fontSize={20} className="animate-spin" />
                  <span>Please wait...</span>
                </div>
              </>
            )}
            {isError && (
              <>
                <DocumentErrorRegular fontSize={72} className="text-foreground/50" />
                <div className="flex items-center gap-x-2">
                  <span>Error loading app</span>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    )
  }

  if (!searchedAppKey?.appKey || !projectId) {
    return (
      <div className="flex size-full items-center justify-center bg-muted">
        <div className="flex flex-col items-center justify-center gap-y-8">
          <DocumentErrorRegular fontSize={72} className="text-foreground/50" />
          <div className="flex flex-col items-center gap-y-2 text-foreground/50">
            <span className="text-sm">App or page not found</span>
            <span className="text-xl">
              <Link className="font-bold underline" to="/workspaces">
                Back to home page
              </Link>
            </span>
          </div>
        </div>
      </div>
    )
  }

  if (!workspace) {
    return null
  }

  return <Content workspace={workspace} />
})

export function Content({ workspace }: { workspace: MessagesWorkspacesWorkspaceCard }) {
  const { pathname } = useLocation()
  const {
    project,
    application,
    organisation,
    // organisation_id,
    privacy_statement_url,
    workspaceId,
    app_key,
    project_id,
  } = workspace
  const workspacePath = `/workspaces/${workspaceId}?returnPage=${pathname}`

  useSetHeaderContext({
    ...DEFAULT_HEADER_STATE,
    title1: application,
    title2: project,
    titleUrl: workspacePath,
    titleNavigation: {
      current: 'application',
      target: workspacePath,
    },
    rightTitle: organisation ? { text: organisation, icon: OrganisationIcon } : undefined,
    privacyUrl: privacy_statement_url ?? undefined,
  })

  const outletContext: PlatformOutletContext = useMemo(
    () => ({
      appId: workspace.app_id,
      appKey: app_key,
      projectId: project_id,
      pageId: pathname,
    }),
    [app_key, pathname, project_id, workspace.app_id]
  )

  return <Outlet context={outletContext} />
}
